import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Clock, ChevronLeft, ChevronRight, MessageCircle, Send, Users, CheckCircle2, ChevronDown, ChevronUp,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TARGET = 480;

const fmtMin = (m) => {
  const h = Math.floor(m / 60);
  const mins = m % 60;
  if (h === 0) return `${mins}m`;
  return mins ? `${h}h ${mins}m` : `${h}h`;
};

const TeamTimesheets = () => {
  const { getAuthHeaders } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [commentText, setCommentText] = useState({});
  const [posting, setPosting] = useState({});

  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/timesheets/team?target_date=${selectedDate}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) setTimesheets(await res.json());
      else setTimesheets([]);
    } catch { toast.error('Failed to load team timesheets'); }
    finally { setLoading(false); }
  }, [selectedDate, getAuthHeaders]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const goDate = (dir) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + dir);
    setSelectedDate(d.toISOString().slice(0, 10));
  };

  const postComment = async (tsId) => {
    const text = (commentText[tsId] || '').trim();
    if (!text) return;
    setPosting((p) => ({ ...p, [tsId]: true }));
    try {
      const res = await fetch(`${API}/api/timesheets/${tsId}/comments`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (res.ok) {
        setCommentText((p) => ({ ...p, [tsId]: '' }));
        fetchTeam();
        toast.success('Comment added');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Network error'); }
    finally { setPosting((p) => ({ ...p, [tsId]: false })); }
  };

  const dateLabel = selectedDate === today ? 'Today' : selectedDate === yesterday ? 'Yesterday' : new Date(selectedDate + 'T00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="space-y-6" data-testid="team-timesheets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100" data-testid="team-ts-title">
            Team Timesheets
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Review submitted timesheets from your team</p>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl px-2 py-1.5">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => goDate(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[140px] text-center">
            {dateLabel} &middot; {selectedDate}
          </span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => goDate(1)} disabled={selectedDate >= today}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-3 text-sm text-zinc-500 dark:text-zinc-400">
        <Users className="h-4 w-4" />
        <span>{timesheets.length} submitted timesheet{timesheets.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : timesheets.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-zinc-400">No submitted timesheets for this date</CardContent></Card>
      ) : (
        <div className="space-y-4" data-testid="team-ts-list">
          {timesheets.map((ts) => {
            const pct = Math.min(100, Math.round((ts.total_minutes / TARGET) * 100));
            const isExpanded = expanded[ts.id];
            return (
              <Card key={ts.id} data-testid={`team-ts-${ts.id}`}>
                <CardContent className="py-4">
                  {/* Summary row */}
                  <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleExpand(ts.id)}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-700 dark:text-blue-300 font-semibold text-sm">
                        {ts.employee_name?.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <p className="font-medium text-zinc-900 dark:text-zinc-100 text-sm">{ts.employee_name}</p>
                        <p className="text-xs text-zinc-500">{ts.entries?.length || 0} tasks &middot; {fmtMin(ts.total_minutes)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        pct >= 100 ? 'bg-emerald-100 text-emerald-700' : pct >= 75 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                      }`}>{pct}%</span>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="mt-4 space-y-3 border-t pt-4">
                      {/* Progress bar */}
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="h-3.5 w-3.5 text-zinc-400" />
                        <div className="flex-1 bg-zinc-100 dark:bg-zinc-800 rounded-full h-2">
                          <div className={`h-2 rounded-full ${pct >= 100 ? 'bg-emerald-500' : pct >= 75 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-zinc-500">{fmtMin(ts.total_minutes)} / {fmtMin(TARGET)}</span>
                      </div>

                      {/* Task entries table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider border-b">
                              <th className="pb-2 pr-3">#</th>
                              <th className="pb-2 pr-3">Task</th>
                              <th className="pb-2 pr-3">Category</th>
                              <th className="pb-2 pr-3">Time</th>
                              <th className="pb-2">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(ts.entries || []).map((e, i) => (
                              <tr key={e.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0">
                                <td className="py-2 pr-3 text-zinc-400">{i + 1}</td>
                                <td className="py-2 pr-3 font-medium text-zinc-800 dark:text-zinc-200">{e.task_name}</td>
                                <td className="py-2 pr-3">
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                                    {e.category || '-'}
                                  </span>
                                </td>
                                <td className="py-2 pr-3 text-zinc-700 dark:text-zinc-300">{fmtMin(e.minutes)}</td>
                                <td className="py-2 text-zinc-500">{e.description || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Comments */}
                      <div className="mt-4 space-y-2">
                        <p className="text-xs font-medium text-zinc-500 flex items-center gap-1.5">
                          <MessageCircle className="h-3.5 w-3.5" /> Comments ({ts.comments?.length || 0})
                        </p>
                        {(ts.comments || []).map((c) => (
                          <div key={c.id} className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg px-3 py-2 text-sm">
                            <span className="font-medium text-zinc-800 dark:text-zinc-200">{c.user_name}</span>
                            <span className="text-zinc-400 text-xs ml-2">{new Date(c.created_at).toLocaleString()}</span>
                            <p className="text-zinc-600 dark:text-zinc-400 mt-0.5">{c.text}</p>
                          </div>
                        ))}
                        {/* Comment input */}
                        <div className="flex gap-2 mt-2">
                          <Input
                            placeholder="Add a comment..."
                            value={commentText[ts.id] || ''}
                            onChange={(e) => setCommentText((p) => ({ ...p, [ts.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && postComment(ts.id)}
                            data-testid={`comment-input-${ts.id}`}
                          />
                          <Button size="icon" className="h-9 w-9 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => postComment(ts.id)} disabled={posting[ts.id]} data-testid={`comment-send-${ts.id}`}>
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamTimesheets;
